import React, { ChangeEvent } from 'react';
import { memo, useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  changeCompanyDeviceAtom,
  changeLowPowerAtom,
} from '../../../models/atoms/company_atoms';
import { changeUserAtom } from '../../../models/atoms/account_atoms';
import { company_controller } from '../../../controllers/company_controller';
import { useTranslation } from '../../../contexts/TranslationContext';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { ConfirmSaveModal } from './ConfirmSaveModal';
import { FormControlLabel, InputAdornment, Stack, Switch } from '@mui/material';
import { DisclaimerText, ErrorText, MHMTypography } from '../MHM/MHMTypography';
import { CheckPermission } from '../CheckPermission';
import { MHMTextField } from '../MHM/MHMTextField';
import { FrequenciesSettings } from './FrequenciesSettings';
import { MAX_MEASUREMENTS } from './DeviceSettings';
import { MHMButton } from '../MHM/MHMButton';
import { SettingsCard } from './SettingsCard';
import { ChangedFrequency } from './ChangedFrequency';
import { useIsMobile } from '../../../hooks/useIsMobile';
/* eslint-disable no-useless-escape */

export const LowPowerSettingsComponent = memo(
  function LowPowerSettingsComponent(
    _: React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
  ) {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const times = useRecoilValue(changeCompanyDeviceAtom);
    const [lowPowerSettings, setLowPowerSettings] =
      useRecoilState(changeLowPowerAtom);
    const changeUser = useRecoilValue(changeUserAtom);
    const canManageDevices = useRecoilValue(canManageDevicesSelector);
    const [lowPowerRelayError, setLowPowerRelayError] = useState('');
    const [lowPowerReadingError, setLowPowerReadingError] = useState<
      [string, { minHours: string; maxHours: string }]
    >(['', { minHours: '', maxHours: '' }]);
    const [batteryThresholdError, setBatteryThresholdError] = useState('');
    const [batteryAlertThresholdError, setBatteryAlertThresholdError] =
      useState('');
    const [totalMeasurementsWarning, setTotalMeasurementsWarning] =
      useState('');
    const [modal, setModal] = useState(false);
    function openModal() {
      setModal(true);
    }
    function closeModal() {
      setModal(false);
    }

    function toggleLowPowerMode(e: ChangeEvent<HTMLInputElement>) {
      if (!canManageDevices) {
        return;
      }

      setLowPowerSettings(prev =>
        prev
          ? {
              ...prev,
              change_low_power: true,
              low_power_on: e.target.checked,
            }
          : prev,
      );
    }

    function handleThresholdChange(e: ChangeEvent<HTMLInputElement>) {
      setBatteryThresholdError('');
      if (lowPowerSettings === null) {
        return;
      }
      if (!/^\d+$/.test(e.target.value)) {
        setBatteryThresholdError(t('Please provide a valid number!'));
        return;
      }
      const parsedValue = Number(e.target.value);
      if (parsedValue < 10 || parsedValue > 60) {
        setBatteryThresholdError(
          t('Not in the allowable range percentage', {
            minPercentage: '10',
            maxPercentage: '60',
          }),
        );
        return;
      }

      setLowPowerSettings(prev =>
        prev
          ? {
              ...prev,
              low_power_battery_threshold: parsedValue,
              change_low_power: true,
            }
          : prev,
      );
    }

    function handleBatteryAlertThresholdChange(
      e: ChangeEvent<HTMLInputElement>,
    ) {
      setBatteryAlertThresholdError('');
      if (lowPowerSettings === null) {
        return;
      }
      if (!/^\d+$/.test(e.target.value)) {
        setBatteryAlertThresholdError(t('Please provide a valid number!'));
        return;
      }
      const parsedValue = Number(e.target.value);
      if (parsedValue < 0 || parsedValue > 100) {
        setBatteryAlertThresholdError(
          t('Not in the allowable range percentage', {
            minPercentage: '0',
            maxPercentage: '100',
          }),
        );
        return;
      }

      setLowPowerSettings(prev =>
        prev
          ? {
              ...prev,
              battery_level_to_warn: parsedValue,
              change_low_power: true,
            }
          : prev,
      );
    }

    async function saveSettings() {
      closeModal();
      if (!lowPowerSettings || !times) {
        return;
      }

      await company_controller.modify_company_low_power_settings(
        lowPowerSettings.low_power_on,
        lowPowerSettings.low_power_battery_threshold,
        lowPowerSettings.battery_level_to_warn,
      );
      company_controller.modify_device_settings(
        null,
        null,
        null,
        null,
        null,
        null,
        times.low_power_message_wait_time_s,
        times.low_power_measurement_sleep_time_s,
      );
    }

    useEffect(() => {
      if (!times) return;

      const totalLowPowerMeasurements =
        times.low_power_message_wait_time_s /
        times.low_power_measurement_sleep_time_s;

      if (totalLowPowerMeasurements > MAX_MEASUREMENTS) {
        setTotalMeasurementsWarning(
          'Total measurements exceed 100! Devices are restricted to 100 measurents per transmission interval.',
        );
      } else {
        setTotalMeasurementsWarning('');
      }
      if (
        times.low_power_message_wait_time_s < 86400 ||
        times.low_power_message_wait_time_s > 5 * 86400
      ) {
        setLowPowerRelayError(
          t('Not in the allowable range days', { minDays: '1', maxDays: '5' }),
        );
      } else {
        setLowPowerRelayError('');
      }
      const maxWaitTime = Math.min(
        72 * 3600,
        times.low_power_message_wait_time_s,
      );
      if (
        times.low_power_measurement_sleep_time_s < 3600 ||
        times.low_power_measurement_sleep_time_s > maxWaitTime
      ) {
        setLowPowerReadingError([
          'Not in the allowable range hours',
          { minHours: '0.1', maxHours: (maxWaitTime / 3600).toFixed(1) },
        ]);
      } else {
        setLowPowerReadingError(['', { minHours: '', maxHours: '' }]);
      }
    }, [times, t]);

    if (!times || !lowPowerSettings) {
      return <></>;
    }

    const isSaveButtonEnabled =
      canManageDevices &&
      (lowPowerSettings.change_low_power || times.change_time) &&
      !lowPowerReadingError[0] &&
      !lowPowerRelayError &&
      !batteryThresholdError &&
      !batteryAlertThresholdError &&
      !totalMeasurementsWarning;

    return (
      <>
        <SettingsCard
          id="low_power_mode"
          title={t('Low Power Mode Settings')}
          sx={{ overflow: 'unset' }}
        >
          <Stack gap={2} p={2}>
            <Stack gap={1}>
              <MHMTypography medium>
                {t('Threshold for low battery alerts')}
              </MHMTypography>
              <DisclaimerText fontSize="small">
                {t('The threshold at which we send a low battery alert.')}
              </DisclaimerText>
              <MHMTextField
                label={t('Battery Alert threshold percentage ')}
                defaultValue={lowPowerSettings.battery_level_to_warn}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    inputMode: 'numeric',
                  },
                }}
                error={Boolean(batteryAlertThresholdError)}
                helperText={
                  <ErrorText component="span">
                    {t(batteryAlertThresholdError)}
                  </ErrorText>
                }
                size="small"
                fullWidth
                disabled={!canManageDevices || !changeUser?.alerts?.low_battery}
                onChange={handleBatteryAlertThresholdChange}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={lowPowerSettings.low_power_on}
                  disabled={!canManageDevices}
                  onChange={toggleLowPowerMode}
                />
              }
              label={
                <MHMTypography medium>{t('Low Power Mode')}</MHMTypography>
              }
            />

            <DisclaimerText fontSize="small">
              {t(
                'Low Power Mode conserves device power until the unit can be replaced, by switching the relay and reading frequencies to be less frequent when the battery percentage of the device falls below the percentage specified below. Each time the device transmits data, the device battery percentage is compared to the defined threshold. The specified Low Power Mode frequencies must be less frequent than the Normal Operation Mode frequencies. High Warning Mode overrides Low Power Mode (and Weather Mode if engaged)',
              )}
            </DisclaimerText>

            {lowPowerSettings.low_power_on && (
              <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
                <CheckPermission
                  hasPermission={canManageDevices}
                  sx={{ flex: 1 }}
                >
                  <MHMTextField
                    label={t('Threshold Battery Percentage')}
                    defaultValue={lowPowerSettings.low_power_battery_threshold}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                        inputMode: 'numeric',
                      },
                    }}
                    error={Boolean(batteryThresholdError)}
                    helperText={
                      <ErrorText component="span">
                        {t(batteryThresholdError)}
                      </ErrorText>
                    }
                    size="small"
                    fullWidth
                    disabled={!canManageDevices}
                    onChange={handleThresholdChange}
                  />
                </CheckPermission>

                <FrequenciesSettings
                  messageTimeKey="low_power_message_wait_time_s"
                  messageTimeUnit="days"
                  messageTimeError={t(lowPowerRelayError)}
                  measurementTimeKey="low_power_measurement_sleep_time_s"
                  measurementTimeUnit="hours"
                  measurementTimeError={t(
                    lowPowerReadingError[0],
                    lowPowerReadingError[1],
                  )}
                  totalMeasurementsError={t(totalMeasurementsWarning, {
                    maxMeasurements: String(MAX_MEASUREMENTS),
                  })}
                  sx={{ flex: 1 }}
                />
              </Stack>
            )}

            <DisclaimerText fontSize="small">
              {t(
                "Device settings are applied to all of the devices in the company's fleet. These settings are communicated to the devices the next time they transmit data.",
              )}
            </DisclaimerText>
            {isSaveButtonEnabled && (
              <ErrorText fontSize="small">
                {t('Changes not yet saved.')}
              </ErrorText>
            )}
            <CheckPermission hasPermission={canManageDevices}>
              <MHMButton disabled={!isSaveButtonEnabled} onClick={openModal}>
                {t('Save')}
              </MHMButton>
            </CheckPermission>
          </Stack>
        </SettingsCard>

        <ConfirmSaveModal
          open={modal}
          onCancel={closeModal}
          onConfirm={saveSettings}
          message={t('Are you sure you want to save these settings?')}
        >
          {lowPowerSettings.low_power_on ? (
            <ChangedFrequency
              title={t('Low Power Mode')}
              messageTimeKey="low_power_message_wait_time_s"
              messageTimeUnit="days"
              measurementTimeKey="low_power_measurement_sleep_time_s"
              measurementTimeUnit="hours"
            />
          ) : null}
        </ConfirmSaveModal>
      </>
    );
  },
);
